
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import { supabaseService } from '../../utils/supabase';

interface ProjectItem {
  id: string;
  name: string;
  description: string;
  technologies: string[];
  startDate: string;
  endDate: string;
  github?: string;
  demo?: string;
  highlights: string[];
  image?: string;
}

export default function Patents() {
  const [projectsData, setProjectsData] = useState<ProjectItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjectsData();
  }, []);

  const loadProjectsData = async () => {
    try {
      const data = await supabaseService.getAllData();
      if (data.projects && data.projects.length > 0) {
        setProjectsData(data.projects);
      }
    } catch (error) {
      console.error('加载项目数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Program & Honor</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              prepare yourself
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && (!projectsData || projectsData.length === 0) && (
            <div className="text-center py-12">
              <i className="ri-code-box-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-600 mb-2">暂无项目</h3>
              <p className="text-gray-500">请通过管理后台添加项目</p>
            </div>
          )}

          {/* Projects List */}
          {!loading && projectsData && projectsData.length > 0 && (
            <div className="space-y-8">
              {projectsData.map((project) => (
              <Card key={project.id} className="p-6 hover:shadow-lg transition-shadow" glass>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Project Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Timeline dot */}
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <i className="ri-code-box-line text-white text-lg"></i>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{project.name}</h3>
                            <p className="text-blue-600 font-medium">{project.startDate} - {project.endDate}</p>
                          </div>
                          <div className="flex space-x-2 mt-2 md:mt-0">
                            {project.github && (
                              <a 
                                href={project.github} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                                title="查看源码"
                              >
                                <i className="ri-github-line"></i>
                              </a>
                            )}
                            {project.demo && (
                              <a 
                                href={project.demo} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                                title="查看演示"
                              >
                                <i className="ri-external-link-line"></i>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-16">
                      <p className="text-gray-700 leading-relaxed mb-4">{project.description}</p>
                      
                      {/* Technologies */}
                      {project.technologies && project.technologies.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-semibold text-gray-900 mb-2">技术栈：</h4>
                          <div className="flex flex-wrap gap-2">
                            {project.technologies.map((tech, index) => (
                              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-200">
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Highlights */}
                      {project.highlights && project.highlights.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">项目亮点：</h4>
                          <div className="grid md:grid-cols-2 gap-2">
                            {project.highlights.map((highlight, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <i className="ri-star-line text-yellow-500 flex-shrink-0"></i>
                                <span className="text-gray-700 text-sm">{highlight}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Project Image */}
                  {project.image && (
                    <div className="lg:w-64 flex-shrink-0">
                      <img 
                        src={project.image} 
                        alt={project.name}
                        className="w-full h-48 lg:h-full object-cover object-top rounded-lg border shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
